import React from 'react';

function returnPath() {
  if (typeof window === 'undefined') return '/';
  return new URLSearchParams(window.location.search).get('from') === 'settings' ? '/settings' : '/';
}

export default function PrivacyPolicy() {
  const backToSettings = returnPath() === '/settings';
  return (
    <div className="app-container" style={{ maxWidth: 900, margin: '0 auto', paddingBottom: 48 }}>
      <button className="back-button" type="button" onClick={() => window.location.assign(returnPath())}>← {backToSettings ? 'Settings' : 'Line Medic'}</button>
      <h1>Line Medic Privacy Policy</h1>
      <p style={{ opacity: .7 }}>Last updated September 23, 2026</p>

      <p>
        Line Medic is an operational platform used by authorized wildland medical programs, members, and administrators.
        Organizations configure their own member access, program resources, accountable equipment, deployment information,
        inventory, and administrative roles within the platform.
      </p>

      <h2>Information we handle</h2>
      <p>
        The application may store account information such as your name, organization email address, account identifier,
        approval status, program roles, verified mobile number, and communication preferences. Operational records may include
        deployment assignments, incidents, gear checked out or returned, inventory and expiration information, barcode or NFC
        identifiers, reported supply use, restock activity, notification audit history, and administrative audit history.
      </p>

      <h2>Operational SMS and mobile information</h2>
      <p>
        Recurring Operational SMS is optional and is off by default. An approved member must actively opt in after verifying
        their mobile number. Operational messages may include Program Feed updates, Prescribed Burn opportunities, roster or
        assignment changes, cancellations, and related program alerts. Message frequency varies and message and data rates may
        apply. Members may reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help, and may also disable
        Operational SMS in Line Medic Settings.
      </p>
      <p>
        Mobile-number verification, one-time passcodes, and other user-requested security messages are separate from recurring
        Operational SMS consent and do not automatically enroll a member in recurring SMS. Mobile numbers, text-messaging
        originator opt-in data, and SMS consent information are not sold, rented, or shared with third parties or affiliates for
        their own marketing or promotional purposes.
      </p>
      <p>
        Service providers that support mobile verification or message delivery may process mobile numbers, message content,
        delivery status, and related technical information only as needed to provide those functions for Line Medic. This
        service-provider processing does not authorize those providers to use Line Medic SMS consent for their own marketing.
      </p>
      <p>
        The <a href="/sms-terms">Operational SMS Terms &amp; Conditions</a> and a public <a href="/sms-consent">carrier-review copy of the in-app consent flow</a> are available from Line Medic.
      </p>

      <h2>Approximate location and Fire Weather</h2>
      <p>
        Fire Weather can use the device location to retrieve official National Weather Service digital forecast data for
        the surrounding area. Before a Fire Weather request is sent, the application reduces the device coordinates to an
        approximate grid appropriate for the resolution of the NWS digital forecast service. The raw device GPS coordinate
        is not added to Line Medic member, deployment, inventory, or administrative records. Approximate location is used
        for app functionality and is not used for advertising, marketing, or tracking.
      </p>
      <p>
        The most recent successful Fire Weather forecast and its approximate forecast-grid location may be saved locally on
        the device so that the last forecast remains available when cell or Wi-Fi service is lost. A saved forecast is
        identified as cached or offline and does not update until network service is available again.
      </p>

      <h2>Saved IAPs and local incident documents</h2>
      <p>
        Saved IAPs lets an authorized member scan or enter an incident-published web link, manually navigate the publisher’s
        own site or folder structure, choose the actual Incident Action Plan PDF, and save that selected document for offline
        field reference. On iPhone, the selected PDF is stored privately inside the Line Medic app container. The application
        may extract the PDF’s existing text layer and store a local search index so the saved document can be searched offline.
      </p>
      <p>
        Saved IAP PDFs and their search text are not intentionally uploaded to the Line Medic document database. The original
        source link is kept locally so the member can return to that publisher later and manually select another operational
        period. Deleting a saved IAP removes its local library record and, on iPhone, its app-private PDF file. The incident
        publisher or hosting provider may receive the normal web request when the member opens or downloads its content.
      </p>

      <h2>Camera and NFC use</h2>
      <p>
        Camera access is used to scan QR codes, including incident IAP links and program equipment tags, Data Matrix codes,
        and product barcodes. NFC access is used to identify program equipment and, where authorized, program NFC tags. Camera frames are processed for scanning and are not
        intentionally uploaded or stored as photographs by Line Medic. Decoded asset or inventory information may be stored
        when an authorized user completes an operational action.
      </p>

      <h2>How information is used</h2>
      <p>
        Information is used to authenticate members, control organization and program access, manage physical equipment and
        inventory, document deployments and returns, support restocking and recalls, maintain audit history, send authorized
        operational communications, and provide field reference tools such as Fire Weather. Line Medic is not supported by
        advertising. Operational data is not sold for advertising or marketing purposes.
      </p>

      <h2>Clinical and patient information</h2>
      <p>
        Line Medic does not provide telemedicine, diagnosis, prescription services, patient billing, or an electronic
        patient medical record. Medication calculators available in internal/web tooling are reference/calculation tools and
        do not create or store a patient record. Inventory records may include medication product, quantity, lot, and expiration
        information for accountable program supplies.
      </p>

      <h2>Service providers</h2>
      <p>
        The platform uses service providers for functions such as application hosting, authentication, database storage,
        operational automation, transactional email, optional operational SMS, push notifications, and official public weather
        data. Those providers process information only as needed to provide the services used by the application. Fire Weather
        uses official NOAA / National Weather Service forecast and observation services.
      </p>

      <h2>Access and public pages</h2>
      <p>
        Operational member features require an approved organization account. Administrative features require additional
        program-admin authorization. Certain pages may be intentionally public, including physical gear recovery pages,
        designated read-only program pages, and carrier-review pages that document the SMS consent process. Public pages are
        designed not to expose private member, medication, inventory, deployment, or patient information.
      </p>

      <h2>Retention and corrections</h2>
      <p>
        Operational and audit records are retained as needed for program administration, equipment accountability,
        inventory history, safety, and applicable recordkeeping requirements. Members who need an account correction or
        access change should contact their Line Medic program administrator. Some operational records may need to be retained
        even after account access is disabled.
      </p>

      <h2>Security</h2>
      <p>
        The application uses authenticated accounts, server-side authorization checks, role-based administrative controls,
        encrypted network connections, and native secure-session storage where supported. Access can be disabled by an
        authorized program administrator.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about a specific organization deployment should be directed to that organization’s configured Line Medic
        administrator or support contact. General app support is available on the <a href="/support">Line Medic Support</a> page.
      </p>
    </div>
  );
}